import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { useWorkspaceStore } from '@/stores'
import { questionnairesApi } from '@/api/endpoints/questionnaires'
import { readFresh } from '@/queries/readFresh'

/**
 * The current workspace's form types (sidebar "Forms" / "Onboarding" entries). Keyed by workspace,
 * so switching workspace never shows another workspace's types. A 403 (no form access) is an empty list.
 * @param {{ enabled?: () => boolean }} [options]
 */
export function useWorkspaceFormTypes({ enabled = /** @type {() => boolean} */ (() => true) } = {}) {
  const workspaceStore = useWorkspaceStore()
  const query = useQuery({
    queryKey: () => ['form-types', workspaceStore.getWorkspaceID],
    queryFn: async () => {
      try {
        const body = await questionnairesApi.formTypes(workspaceStore.getWorkspaceID)
        const types = body?.data?.types ?? []
        return Array.isArray(types) ? types : []
      } catch (error) {
        // Keep the sidebar usable when the user lacks form access
        if (error.response?.status === 403) return []
        throw error
      }
    },
    enabled: () => Boolean(workspaceStore.getWorkspaceID) && enabled(),
    staleTime: Infinity,
  })
  return {
    formTypes: computed(() => query.data.value ?? []),
    loadingFormTypes: query.isPending,
  }
}

/** The default questionnaire template for a workspace form type (`employees`, `vendors`, a party type…). */
export function fetchWorkspaceQuestionnaires(type, params) {
  const workspaceId = useWorkspaceStore().getWorkspaceID
  return readFresh(['questionnaires', 'workspace', workspaceId, type, params], async () =>
    (await questionnairesApi.workspaceForms(workspaceId, type, params)).data,
  )
}
